import axios from "axios";
import { getToken, isTokenExpired, clearSessionAndRedirectToLogin } from "../utils/token";
import { getTenantId } from "../utils/tenant";
import { rewriteAlbToSameOrigin } from "../utils/apiEnv";

const isDev = process.env.NODE_ENV === "development";

function isLocalDevHost() {
	return (
		typeof window !== "undefined" &&
		/^(localhost|127\.0\.0\.1)$/i.test(String(window.location.hostname || ""))
	);
}

function preferProxyInDev() {
	return isDev && isLocalDevHost() && String(process.env.REACT_APP_PREFER_PROXY_IN_DEV || "") === "1";
}

/**
 * Resolve microservice base URL at call time (not module load) so production
 * rewrite to insurance.highwatch.ai gateway always uses the live hostname.
 */
export function resolveServiceBaseUrl({ envKey, proxyPath }) {
	if (preferProxyInDev() && proxyPath) {
		return String(proxyPath).replace(/\/+$/, "");
	}
	const configured = String(process.env[envKey] || "").trim();
	return rewriteAlbToSameOrigin(configured, proxyPath);
}

export function getAuthApiBaseUrl() {
	return resolveServiceBaseUrl({
		envKey: "REACT_APP_AUTH_API_URL",
		proxyPath: "/backend/api/auth"
	});
}

export function getIntelligenceApiBaseUrl() {
	return resolveServiceBaseUrl({
		envKey: "REACT_APP_INTELLIGENCE_API_URL",
		proxyPath: "/backend/api/intelligence/v1"
	});
}

function requestPath(config) {
	const raw = String(config?.url || "");
	if (/^https?:\/\//i.test(raw)) {
		try {
			return new URL(raw).pathname;
		} catch {
			return raw.split("?")[0];
		}
	}
	return raw.split("?")[0];
}

function isAuthPublicPath(path) {
	return /^\/(login|register|forgot-password|reset-password)(\/|$)/.test(path);
}

const pendingControllers = new Set();

function trackAbortController(config) {
	const controller = new AbortController();
	const outer = config.signal;
	if (outer) {
		if (outer.aborted) controller.abort();
		else {
			outer.addEventListener("abort", () => controller.abort(), { once: true });
		}
	}
	config.signal = controller.signal;
	config.__hwController = controller;
	const entry = { controller, method: String(config.method || "get").toLowerCase() };
	config.__hwPendingEntry = entry;
	pendingControllers.add(entry);
	return controller;
}

function untrackAbortController(config) {
	const entry = config?.__hwPendingEntry;
	if (entry) pendingControllers.delete(entry);
	const controller = config?.__hwController;
	if (controller) {
		for (const item of pendingControllers) {
			if (item.controller === controller) pendingControllers.delete(item);
		}
	}
}

/** Cancel in-flight GETs so a route change (or DELETE) is not queued behind hung Overview requests. */
export function abortPendingRequests({ onlyGet = true } = {}) {
	for (const entry of [...pendingControllers]) {
		if (onlyGet && entry.method !== "get") continue;
		try {
			entry.controller.abort();
		} catch {}
		pendingControllers.delete(entry);
	}
}

function isCanceled(error) {
	return error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";
}

/**
 * @param {{ getBaseURL: () => string, service: "auth" | "intelligence" }} options
 */
export function createApiClient({ getBaseURL, service }) {
	const api = axios.create({
		timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS) || 600000
	});

	api.interceptors.request.use(
		(config) => {
			config.baseURL = getBaseURL();
			const path = requestPath(config);
			const isPublic = service === "auth" && isAuthPublicPath(path);

			if (!isPublic) {
				const token = getToken();
				if (token && isTokenExpired(token)) {
					clearSessionAndRedirectToLogin();
					return Promise.reject(new Error("Session expired"));
				}
				if (token) {
					config.headers = config.headers ?? {};
					config.headers.Authorization = `Bearer ${token}`;
				}
			}

			const tenantId = getTenantId();
			if (tenantId) {
				config.headers = config.headers ?? {};
				if (!("X-Tenant-ID" in config.headers) && !("x-tenant-id" in config.headers)) {
					config.headers["X-Tenant-ID"] = tenantId;
				}
			}

			trackAbortController(config);
			return config;
		},
		(error) => Promise.reject(error)
	);

	api.interceptors.response.use(
		(response) => {
			untrackAbortController(response?.config);
			return response;
		},
		(error) => {
			untrackAbortController(error?.config || error?.response?.config);
			if (isCanceled(error)) {
				return Promise.reject(error);
			}
			const status = error?.response?.status;
			const path = requestPath(error?.config || {});
			const onLoginPage =
				typeof window !== "undefined" && /^\/login\b/.test(window.location.pathname || "");
			const isPublic = service === "auth" && isAuthPublicPath(path);
			const token = getToken();

			// Expired / invalid session: any authenticated 401 → login (avoids "Network Error" toast)
			if (status === 401 && !onLoginPage && !isPublic && token) {
				clearSessionAndRedirectToLogin();
				return Promise.reject(error);
			}

			// CORS / opaque failures often surface as "Network Error" with no status after JWT dies
			if (!status && !onLoginPage && !isPublic && token && isTokenExpired(token)) {
				clearSessionAndRedirectToLogin();
				return Promise.reject(error);
			}

			if (status === 403) {
				const errorMessage = String(error?.response?.data?.message || error?.message || "");
				if (errorMessage.includes("Multi-Factor Authentication (MFA) is required") && !onLoginPage) {
					let mfaEnabledFlag = false;
					try {
						mfaEnabledFlag = localStorage.getItem("mfaEnabled") === "1";
					} catch {}
					if (typeof window !== "undefined") {
						const alreadyOnMfa = /\/settings\/security\/mfa-setup\b/.test(window.location.pathname);
						let redirected = false;
						try {
							redirected = sessionStorage.getItem("mfaRedirected") === "1";
						} catch {}
						if (mfaEnabledFlag) {
							return Promise.reject(error);
						}
						if (!alreadyOnMfa && !redirected) {
							try {
								sessionStorage.setItem("mfaRedirected", "1");
								const current =
									window.location.pathname +
									(window.location.search || "") +
									(window.location.hash || "");
								sessionStorage.setItem("mfaReturnTo", current);
							} catch {}
							window.location.href = "/settings/security/mfa-setup";
						}
					}
				}
			}

			return Promise.reject(error);
		}
	);

	return api;
}

/** @deprecated use getAuthApiBaseUrl() — kept for chat.api import compat */
export const AUTH_API_BASE_URL = "";

/** @deprecated use getIntelligenceApiBaseUrl() */
export const INTELLIGENCE_API_BASE_URL = "";
